import { useCallback, useEffect, useState } from 'react';
import { Role2, UserGroup } from '../../../models';
import { apiClient, apiErrorMessage } from '../../../utils';
import { Button, Modal, Table, Select, Input, message } from 'antd';
import { ExclamationCircleOutlined, PlusOutlined, SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { TableNameColumn } from '../../../elements';
import { RolePermissionsSummary } from '../roles2';

export function UserGroupRolesForm(props: { userGroup?: UserGroup }) {
  const [allRoles, setAllRoles] = useState<Role2[]>();
  const [roles, setRoles] = useState<Role2[]>();
  const [rolesLoading, setRolesLoading] = useState(false);
  const [allRolesLoading, setAllRolesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');

  const loadRoles = useCallback(() => {
    setRolesLoading(true);
    apiClient
      .get(`authorization/roles/user-groups/${props.userGroup?.id}?minimal=true`)
      .then((response: any) => {
        setRoles(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setRolesLoading(false);
      });
  }, [props.userGroup?.id]);

  useEffect(() => {
    if (props.userGroup?.id) {
      loadRoles();
    }
  }, [loadRoles, props.userGroup?.id]);

  const loadAllRoles = () => {
    if (allRoles) return; // Don't reload if already loaded

    setAllRolesLoading(true);
    apiClient
      .get('authorization/roles')
      .then((response: any) => {
        setAllRoles(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setAllRolesLoading(false);
      });
  };

  const handleAddModalOpen = () => {
    setAddModalVisible(true);
    setSelectedRoleIds([]);
    loadAllRoles();
  };

  const confirmDeleteRole = (roleId: string) => {
    Modal.confirm({
      title: 'Rol sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Rolü kullanıcı grubundan silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: () => removeRole(roleId),
    });
  };

  const removeRole = (roleId: string) => {
    setDeleting(true);
    apiClient
      .delete(`authorization/roles/${roleId}/user-groups/${props.userGroup?.id}`)
      .then(() => {
        message.success('Rol başarıyla silindi');
        loadRoles();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setDeleting(false);
      });
  };

  const addRoles = () => {
    if (!selectedRoleIds.length) return;

    setSubmitting(true);
    apiClient
      .post(`authorization/roles/user-group-assignments`, { role2Ids: selectedRoleIds, userGroupIds: [props.userGroup?.id] })
      .then(() => {
        message.success(`${selectedRoleIds.length} rol başarıyla eklendi`);
        setAddModalVisible(false);
        setSelectedRoleIds([]);
        loadRoles();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const tableColumns = [
    {
      title: 'İsim',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, role: Role2) => (
        <TableNameColumn name={name} description={role.description} path={'/admin/roles2/' + role.id} />
      ),
    },
    {
      title: 'Yetkiler',
      dataIndex: 'permissions2',
      key: 'permissions',
      render: (_: any[], role: Role2) => <RolePermissionsSummary role={role} maxDisplay={10} />,
    },
    {
      title: '',
      key: 'action',
      render: (_: any, role: Role2) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <CloseOutlined
            className="text-red-500 cursor-pointer hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => !deleting && confirmDeleteRole(role.id)}
            style={{ opacity: deleting ? 0.5 : 1 }}
            disabled={deleting}
          />
        </div>
      ),
    },
  ];

  const filteredRoles = roles?.filter(
    role =>
      role.name.toLowerCase().includes(searchText.toLowerCase()) ||
      role.description?.toLowerCase().includes(searchText.toLowerCase()) ||
      role.permissions2.some(permission => permission.targetLabel?.toLowerCase().includes(searchText.toLowerCase())),
  );

  const availableRoles = allRoles?.filter((role: Role2) => !roles?.some((assignedRole: Role2) => assignedRole.id === role.id));

  return (
    <div>
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddModalOpen}>
          Rol ekle
        </Button>
        <div>
          <Input
            placeholder="Arama"
            prefix={<SearchOutlined />}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />
        </div>
      </div>

      <Table columns={tableColumns} dataSource={filteredRoles} rowKey="id" loading={rolesLoading} pagination={false} />

      <Modal
        title="Rol ekle"
        visible={addModalVisible}
        onOk={addRoles}
        onCancel={() => {
          setAddModalVisible(false);
          setSelectedRoleIds([]);
        }}
        okButtonProps={{ disabled: !selectedRoleIds.length, loading: submitting }}
      >
        <Select
          mode="multiple"
          showSearch
          style={{ width: '100%' }}
          placeholder="Rol seçin"
          onChange={value => setSelectedRoleIds(value)}
          value={selectedRoleIds}
          loading={allRolesLoading}
          filterOption={(input, option) => {
            const role = availableRoles?.find(u => u.id === option?.value);
            if (!role) return false;
            return role.name.toLowerCase().includes(input.toLowerCase()) || role.description?.toLowerCase().includes(input.toLowerCase());
          }}
        >
          {availableRoles?.map(role => (
            <Select.Option key={role.id} value={role.id}>
              {role.name}
            </Select.Option>
          ))}
        </Select>
      </Modal>
    </div>
  );
}
